'use client';

import React from 'react';
import SlideWrapper from '../SlideWrapper';
import { Sun, Moon, Leaf, Flame, Star, Compass, Award, Anchor } from 'lucide-react';

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
}

const dayItems = [
  {
    day: 'Ngày 1',
    icon: <Leaf size={16} className="text-[#4caf7d]" />,
    title: 'Khởi động & Làm sạch',
    desc: 'Check-in, Green Volunteer, thả cá, BBQ & lửa trại',
    color: '#4caf7d',
  },
  {
    day: 'Ngày 2',
    icon: <Compass size={16} className="text-[#e8a030]" />,
    title: 'Cam kết xanh & Cầu tầm vông',
    desc: 'Cây cam kết, hoàn thiện dòng nước, làm cầu tầm vông',
    color: '#e8a030',
  },
  {
    day: 'Ngày 3',
    icon: <Sun size={16} className="text-[#a8d87a]" />,
    title: 'Làm sạch đảo 2 & Báu vật',
    desc: 'Green Volunteer chặng 2, setup khu truy tìm báu vật, đêm tài năng',
    color: '#a8d87a',
  },
  {
    day: 'Ngày 4',
    icon: <Star size={16} className="text-[#f5c96a]" />,
    title: 'Hoàn thiện & Trồng cây',
    desc: 'Hoàn thiện báu vật & dòng nước, trồng cây xanh, Gala Dinner',
    color: '#f5c96a',
  },
  {
    day: 'Ngày 5',
    icon: <Flame size={16} className="text-[#e8a030]" />,
    title: 'Vượt chướng ngại & Vận hành',
    desc: 'Xây khu vượt chướng ngại, test toàn bộ khu trải nghiệm, đêm chia sẻ thật',
    color: '#e8a030',
  },
  {
    day: 'Ngày 6',
    icon: <Moon size={16} className="text-[#4caf7d]" />,
    title: 'Tự do & Team Building',
    desc: 'Ghi hình tự do, team building, đêm thi tài năng tổng hợp',
    color: '#4caf7d',
  },
  {
    day: 'Ngày 7',
    icon: <Award size={16} className="text-[#a8d87a]" />,
    title: 'Tổng kết & Trao giải',
    desc: 'Hoàn thiện clip, tổng kết, trao chứng nhận, lửa trại kết thúc',
    color: '#a8d87a',
  },
  {
    day: 'Ngày 8',
    icon: <Anchor size={16} className="text-[#f5c96a]" />,
    title: 'Lên thuyền về đất liền',
    desc: 'Ăn sáng, chuẩn bị hành lý, lên thuyền kết thúc hành trình',
    color: '#f5c96a',
  },
];

export default function Slide07JourneyOverview({ id, registerRef }: Props) {
  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#0a1a0f]">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1663535016269-c8946cb8fb57"
          alt="Hành trình khám phá thiên nhiên hùng vĩ, con đường mòn xuyên rừng núi xanh tươi"
          className="w-full h-full object-cover opacity-8"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a3a2a]/80 via-[#0a1a0f] to-[#0a1a0f]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(232,160,48,0.4)] to-transparent" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-24 pt-28 min-h-screen flex flex-col justify-center">
        <div className="fade-in-up mb-4">
          <div className="chapter-badge amber">
            <Sun size={12} />
            Hành trình 8 ngày 7 đêm
          </div>
        </div>

        <div className="fade-in-up mb-3">
          <h2 className="font-display font-800 text-[clamp(28px,4.5vw,56px)] leading-[1.1] text-[#f0ede6] tracking-tight">
            Overview hành trình
          </h2>
          <h2 className="font-display font-800 text-[clamp(28px,4.5vw,56px)] leading-[1.1] tracking-tight text-gradient-amber">
            8 ngày 7 đêm
          </h2>
        </div>

        <div className="fade-in-up mb-10">
          <div className="w-[60px] h-[3px] bg-gradient-to-r from-[#e8a030] to-[#f5c96a] rounded-full" />
        </div>

        <div className="relative fade-in-up">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {dayItems.map((item, i) => (
              <div
                key={`day-overview-${i}`}
                className="rounded-2xl p-5 bg-[rgba(26,58,42,0.4)] border border-[rgba(76,175,125,0.15)] transition-all duration-300 hover:-translate-y-1 relative overflow-hidden"
              >
                <div
                  className="absolute top-0 right-0 w-16 h-16 rounded-full blur-2xl opacity-20"
                  style={{ background: item.color }}
                />
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-lg" style={{ background: `${item.color}20` }}>
                    {item.icon}
                  </div>
                  <span
                    className="text-[11px] font-700 uppercase tracking-wider font-body"
                    style={{ color: item.color }}
                  >
                    {item.day}
                  </span>
                </div>
                <h3 className="font-display font-700 text-[14px] text-[#f0ede6] mb-2">{item.title}</h3>
                <p className="font-body text-[12px] text-[#a0b8a8] leading-relaxed">{item.desc}</p>
                <div
                  className="absolute bottom-0 left-0 h-0.5 w-full opacity-40"
                  style={{ background: `linear-gradient(90deg, ${item.color}, transparent)` }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
}
